import { DateTime } from "luxon";
import FinancialAccount from "../FinancialAccount";
import DateAndValue from "../DateAndValue";
import ProfitAndLoss from "../ProfitAndLoss";
import GeneralHelper from "../../utils/GeneralHelper";
import { ASCENDANT_ORDER, DESCENDANT_ORDER } from "../../config";

export default class MonthlyProfitAndLoss {
  /**
   * @param {array<FinancialAccount>} incomes
   * @param {array<FinancialAccount>} expenses
   * @param {array<FinancialAccount>} otherIncomes
   * @param {array<FinancialAccount>} otherExpenses
   * @param {array<FinancialAccount>} costOfSales
   * @param {array<FinancialAccount>} fromDate
   * @param {array<FinancialAccount>} toDate
   * @param {array<FinancialAccount>} netOperatingProfit
   * @param {array<FinancialAccount>} netOtherIncome
   * @param {array<FinancialAccount>} netProfit
   * @param {array<FinancialAccount>} grossProfit
   * @param {array<string>} dates
   */
  constructor({
    incomes = [],
    expenses = [],
    otherIncomes = [],
    otherExpenses = [],
    costOfSales = [],
    netOperatingProfits = [],
    netOtherIncomes = [],
    netProfits = [],
    grossProfits = [],
    dates = [],
    dateOrder = DESCENDANT_ORDER,
  }) {
    this.incomes = incomes;
    this.expenses = expenses;
    this.otherIncomes = otherIncomes;
    this.otherExpenses = otherExpenses;
    this.costOfSales = costOfSales;
    this.netOperatingProfits = netOperatingProfits;
    this.netOtherIncomes = netOtherIncomes;
    this.netProfits = netProfits;
    this.grossProfits = grossProfits;
    this.dates = dates;
    this.dateOrder = dateOrder;
  }

  get ascendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
    });
  }

  get descendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return -1;
      if (a < b) return 1;
    });
  }

  get orderedDates() {
    if (this.dateOrder === ASCENDANT_ORDER) {
      return this.ascendantDates;
    }

    return this.descendantDates;
  }

  get datesFormatted() {
    return this.orderedDates.map((date) => {
      return date;
    });
  }

  get EBITDAAsDefinedHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.EBITDAAsDefined,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get datesFormatted() {
    return this.orderedDates.map((date) => {
      return DateTime.fromISO(date).toFormat("LLLL y");
    });
  }

  get yearsFormatted() {
    return this.yearlyTotalSales.map((obj) => obj.date);
  }

  get totalSales() {
    //This is because interest income is not sales indeed
    const businessRevenues = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getIncomeCategories().BUSINESS_REVENUE_CATEGORY.value,
        "incomes"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const businessRevenue = businessRevenues[date];

      return {
        date: date.split("T")[0],
        value: businessRevenue || 0,
      };
    });
  }

  get yearlyTotalSales() {
    return this.calculateTotalByYear(this.totalSales);
  }

  get totalLaborCost() {
    //we need it for Bizminer ratio
    const laborCost = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getCostOfSalesCategories().COST_OF_SALES_LABOR_CATEGORY.value,
        "costofsales"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const totalLaborCost = laborCost[date];

      return {
        date,
        value: totalLaborCost || 0,
      };
    });
  }

  get yearlyTotalLaborCost() {
    return this.calculateTotalByYear(this.totalLaborCost);
  }

  get totalSalaryWage() {
    //we need it for Bizminer ratio
    const salaryWage = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getExpensesCategories().SALARY_WAGES.value,
        "expenses"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const totalSalaryWage = salaryWage[date];

      return {
        date,
        value: totalSalaryWage || 0,
      };
    });
  }

  get yearlyTotalSalaryWage() {
    return this.calculateTotalByYear(this.totalSalaryWage);
  }

  get totalOfficersCompensation() {
    //we need it for Bizminer ratio
    const officersCompensation = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getExpensesCategories().OFFICERS_COMPENSATION_CATEGORY.value,
        "expenses"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const totalOfficersCompensation = officersCompensation[date];

      return {
        date,
        value: totalOfficersCompensation || 0,
      };
    });
  }

  get yearlyTotalOfficersCompensation() {
    return this.calculateTotalByYear(this.totalOfficersCompensation);
  }

  get totalExpenses() {
    return this.groupByDate(this.expenses);
  }

  get totalOperatingExpenses() {
    const {
      OFFICERS_COMPENSATION_CATEGORY,
      SALARY_WAGES,
      RENT_CATEGORY,
      TAXES_PAID_CATEGORY,
      ADVERTISING_CATEGORY,
      BENEFITS_PENSION_CATEGORY,
      SALES_AND_GENERAL_AND_ADMINISTRATIVE,
      REPAIRS_CATEGORY,
      BAD_DEBT_EXPENSE_CATEGORY,
      AMORTIZATION_DEPRECIATION_DEPLETION_CATEGORY,
    } = ProfitAndLoss.getExpensesCategories();

    const operatingExpenseCategories = [
      OFFICERS_COMPENSATION_CATEGORY.value,
      SALARY_WAGES.value,
      RENT_CATEGORY.value,
      TAXES_PAID_CATEGORY.value,
      ADVERTISING_CATEGORY.value,
      BENEFITS_PENSION_CATEGORY.value,
      SALES_AND_GENERAL_AND_ADMINISTRATIVE.value,
      REPAIRS_CATEGORY.value,
      BAD_DEBT_EXPENSE_CATEGORY.value,
      AMORTIZATION_DEPRECIATION_DEPLETION_CATEGORY.value,
    ];

    const operatingExpenses = this.expenses.filter((expense) => {
      return operatingExpenseCategories.includes(expense.category);
    });

    return this.groupByDate(operatingExpenses);
  }

  get yearlyTotalExpenses() {
    return this.calculateTotalByYear(this.totalExpenses);
  }

  get yearlyTotalOperatingExpenses() {
    return this.calculateTotalByYear(this.totalOperatingExpenses);
  }

  get totalCostOfSales() {
    return this.groupByDate(this.costOfSales);
  }

  get yearlyTotalCostOfSales() {
    return this.calculateTotalByYear(this.totalCostOfSales);
  }

  get totalGrossProfits() {
    const totalSales = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalSales,
      "date",
      "value"
    );

    const totalCostOfSales =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.totalCostOfSales,
        "date",
        "value"
      );

    return this.orderedDates.map((date) => {
      const formattedDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      const sales = totalSales[formattedDate] || 0;
      const costOfSales = totalCostOfSales[formattedDate] || 0;

      return new DateAndValue({
        date: DateTime.fromISO(date).toFormat("yyyy-LL-dd"),
        value: sales - costOfSales,
      });
    });
  }

  get businessRevenue() {
    const businessRevenues = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getIncomeCategories().BUSINESS_REVENUE_CATEGORY.value,
        "incomes"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const businessRevenue = businessRevenues[date];

      return {
        date,
        value: businessRevenue || 0,
      };
    });
  }

  get yearlyTotalGrossProfits() {
    return this.calculateTotalByYear(this.totalGrossProfits);
  }

  get totalNetOperatingProfits() {
    return this.groupByDate(this.netOperatingProfits);
  }

  get totalOperatingIncome() {
    const totalGrossProfits = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalGrossProfits,
      "date",
      "value"
    );

    const totalOperatingExpenses =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.totalOperatingExpenses,
        "date",
        "value"
      );

    return this.orderedDates.map((date) => {
      const formattedDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      const grossProfits = totalGrossProfits[formattedDate] || 0;
      const operatingExpenses = totalOperatingExpenses[formattedDate] || 0;

      return new DateAndValue({
        date: DateTime.fromISO(date).toFormat("yyyy-LL-dd"),
        value: grossProfits - operatingExpenses,
      });
    });
  }

  get yearlyTotalOperatingIncome() {
    return this.calculateTotalByYear(this.totalOperatingIncome);
  }

  get totalOtherIncomes() {
    return this.groupByDate(this.otherIncomes);
  }

  get yearlyTotalOtherIncomes() {
    return this.calculateTotalByYear(this.totalOtherIncomes);
  }

  get totalOtherExpenses() {
    return this.groupByDate(this.otherExpenses);
  }

  get yearlyTotalOtherExpenses() {
    return this.calculateTotalByYear(this.totalOtherExpenses);
  }

  get totalInterestIncome() {
    const interestIncomes = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getIncomeCategories().INTEREST_INCOME_CATEGORY.value,
        "incomes"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const interestIncome = interestIncomes[date];

      return {
        date,
        value: interestIncome || 0,
      };
    });
  }

  get yearlyTotalInterestIncome() {
    return this.calculateTotalByYear(this.totalInterestIncome);
  }

  get totalInterestExpense() {
    const interestExpenses = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getExpensesCategories().INTEREST_EXPENSE_CATEGORY.value,
        "expenses"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const interestExpense = interestExpenses[date];

      return {
        date,
        value: interestExpense || 0,
      };
    });
  }

  get yearlyTotalInterestExpense() {
    return this.calculateTotalByYear(this.totalInterestExpense);
  }

  get totalPreTaxNetProfits() {
    const earningsBeforeIncomeTaxes =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.earningsBeforeIncomeTaxes,
        "date",
        "value"
      );

    const totalInterestIncome = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalInterestIncome,
      "date",
      "value"
    );

    const totalInterestExpense = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalInterestExpense,
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const earningBeforeIncomeTaxes = earningsBeforeIncomeTaxes[date] || 0;
      const interestIncome = totalInterestIncome[date] || 0;
      const interestExpense = totalInterestExpense[date] || 0;

      return new DateAndValue({
        date,
        value: earningBeforeIncomeTaxes + interestIncome - interestExpense,
      });
    });
  }

  get yearlyTotalPreTaxNetProfits() {
    return this.calculateTotalByYear(this.totalPreTaxNetProfits);
  }

  get earningsBeforeIncomeTaxes() {
    const totalOperatingIncome = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalOperatingIncome,
      "date",
      "value"
    );

    const totalOtherIncomes = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalOtherIncomes,
      "date",
      "value"
    );

    const totalOtherExpenses = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalOtherExpenses,
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const formattedDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      const operatingIncome = totalOperatingIncome[formattedDate] || 0;
      const otherIncome = totalOtherIncomes[formattedDate] || 0;
      const otherExpense = totalOtherExpenses[formattedDate] || 0;

      return new DateAndValue({
        date,
        value: operatingIncome + otherIncome - otherExpense,
      });
    });
  }

  get yearlyEarningsBeforeIncomeTaxes() {
    return this.calculateTotalByYear(this.earningsBeforeIncomeTaxes);
  }

  get totalIncomeTax() {
    const incomeTaxes = GeneralHelper.convertArrayToKeyAndValueObject(
      this.getCategoryValues(
        ProfitAndLoss.getExpensesCategories().INCOME_TAX_EXPENSE_CATEGORY.value,
        "expenses"
      ),
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const incomeTax = incomeTaxes[date];

      return {
        date,
        value: incomeTax || 0,
      };
    });
  }

  get yearlyTotalIncomeTax() {
    return this.calculateTotalByYear(this.totalIncomeTax);
  }

  get totalDepreciationDepletionAndAmortization() {
    const depreciationDepletionAndAmortization =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.getCategoryValues(
          ProfitAndLoss.getExpensesCategories()
            .AMORTIZATION_DEPRECIATION_DEPLETION_CATEGORY.value,
          "expenses"
        ),
        "date",
        "value"
      );

    return this.orderedDates.map((date) => {
      const DDA = depreciationDepletionAndAmortization[date];

      return {
        date,
        value: DDA || 0,
      };
    });
  }

  get yearlyTotalDepreciationDepletionAndAmortization() {
    return this.calculateTotalByYear(
      this.totalDepreciationDepletionAndAmortization
    );
  }

  get totalDefinitionAdjustments() {
    const monthlyInterest = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalInterestExpense,
      "date",
      "value"
    );

    const monthlyTaxes = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalIncomeTax,
      "date",
      "value"
    );

    const monthlyDepreciationDepletionAndAmortization =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.totalDepreciationDepletionAndAmortization,
        "date",
        "value"
      );

    return this.orderedDates.map((date) => {
      const DDA = monthlyDepreciationDepletionAndAmortization[date];
      const taxes = monthlyTaxes[date];
      const interest = monthlyInterest[date];

      return {
        date,
        value: DDA + taxes + interest || 0,
      };
    });
  }

  get yearlyTotalDefinitionAdjustments() {
    return this.calculateTotalByYear(this.totalDefinitionAdjustments);
  }

  get EBITDAAsDefined() {
    const monthlyAfterTaxNetProfit =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.totalAfterTaxNetProfits,
        "date",
        "value"
      );

    const monthlyTotalDefinitionAdjustments =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.totalDefinitionAdjustments,
        "date",
        "value"
      );

    return this.orderedDates.map((date) => {
      const afterTaxNetProfit = monthlyAfterTaxNetProfit[date];
      const totalDefinitionAdjustments =
        monthlyTotalDefinitionAdjustments[date];

      return {
        date,
        value: afterTaxNetProfit + totalDefinitionAdjustments || 0,
      };
    });
  }

  get yearlyEBITDAAsDefined() {
    return this.calculateTotalByYear(this.EBITDAAsDefined);
  }

  //get data ascendant EBITDA LTMs
  get unsortedEBITDALastTwelveMonth() {
    const TWELVE_MONTHS = 12;
    const { EBITDAAsDefinedHash } = this;

    return this.ascendantDates.map((date, index) => {
      //EBITDA LTM comes form earlier 12 months' EBITDA regardless dateorder
      let value = 0;
      let disabled = true;

      if (index >= TWELVE_MONTHS) {
        for (var i = 0; i < TWELVE_MONTHS; i++) {
          value +=
            EBITDAAsDefinedHash[
              DateTime.fromISO(this.ascendantDates[index - i - 1]).toFormat(
                "yyyy-LL-dd"
              )
            ];
        }
        disabled = false;
      }

      return new DateAndValue({
        date,
        value,
        disabled,
      });
    });
  }

  //get sorted EBITDA LTMs by dateorder to be sent to dashboard
  get EBITDALastTwelveMonth() {
    return this.unsortedEBITDALastTwelveMonth.sort((a, b) => {
      if (
        (this.dateOrder === ASCENDANT_ORDER && a.date > b.date) ||
        (this.dateOrder === DESCENDANT_ORDER && a.date < b.date)
      )
        return 1;
      else return -1;
    });
  }

  get totalAfterTaxNetProfits() {
    const totalPreTaxNetProfits =
      GeneralHelper.convertArrayToKeyAndValueObject(
        this.totalPreTaxNetProfits,
        "date",
        "value"
      );

    const totalIncomeTax = GeneralHelper.convertArrayToKeyAndValueObject(
      this.totalIncomeTax,
      "date",
      "value"
    );

    return this.orderedDates.map((date) => {
      const preTaxNetProfits = totalPreTaxNetProfits[date];
      const incomeTax = totalIncomeTax[date];

      return new DateAndValue({
        date,
        value: preTaxNetProfits - incomeTax,
      });
    });
  }

  get yearlyTotalAfterTaxNetProfits() {
    return this.calculateTotalByYear(this.totalAfterTaxNetProfits);
  }

  get grossProfitMargins() {
    return this.calculateMargins("totalGrossProfits");
  }

  get yearlyGrossProfitMargins() {
    return this.calculateMargins("yearlyTotalGrossProfits", { yearly: true });
  }

  get EBITDALastTwelveMonthMargins() {
    //This can't be calculated as normal margins because EBITDA LTM must be divided by the sum of earlier 12 month's total sales(or incomes)
    const TWELVE_MONTHS = 12;
    const length = this.incomes.length;

    return this.unsortedEBITDALastTwelveMonth
      .map((unsortedEBITDALTM, index) => {
        let value = 0;
        let salesSum = 0;

        if (index >= TWELVE_MONTHS) {
          //calculate sum of earlier 12 months' totalsales(income)..
          for (var i = 1; i <= TWELVE_MONTHS; i++)
            salesSum += this.incomes[length - index - 1 + i].value; //unsortedEBITDALastTwelveMonth : date ascendant, incomes : date descendant..
          value =
            Number(((unsortedEBITDALTM.value / salesSum) * 100).toFixed(2)) ||
            0;
        }

        return new DateAndValue({
          ...unsortedEBITDALTM,
          value,
        });
      })
      .sort((a, b) => {
        //sort date ascendant EBITDA LTM Margins by dateorder
        if (
          (this.dateOrder === ASCENDANT_ORDER && a.date > b.date) ||
          (this.dateOrder === DESCENDANT_ORDER && a.date < b.date)
        )
          return 1;
        else return -1;
      });
  }

  get operExpenseMargins() {
    return this.calculateMargins("totalOperatingExpenses");
  }

  get yearlyOperExpenseMargins() {
    return this.calculateMargins("yearlyTotalOperatingExpenses", {
      yearly: true,
    });
  }

  get operIncomeMargins() {
    return this.calculateMargins("totalOperatingIncome");
  }

  get yearlyOperIncomeMargins() {
    return this.calculateMargins("yearlyTotalOperatingIncome", {
      yearly: true,
    });
  }

  get otherIncomeMargins() {
    return this.calculateMargins("totalOtherIncomes");
  }

  get yearlyOtherIncomeMargins() {
    return this.calculateMargins("yearlyTotalOtherIncomes", { yearly: true });
  }

  get otherExpensesMargins() {
    return this.calculateMargins("totalOtherExpenses");
  }

  get yearlyOtherExpensesMargins() {
    return this.calculateMargins("yearlyTotalOtherExpenses", { yearly: true });
  }

  get EBITMargins() {
    return this.calculateMargins("earningsBeforeIncomeTaxes");
  }

  get yearlyEBITMargins() {
    return this.calculateMargins("yearlyEarningsBeforeIncomeTaxes", {
      yearly: true,
    });
  }

  get EBTMargins() {
    return this.calculateMargins("totalPreTaxNetProfits");
  }

  get yearlyEBTMargins() {
    return this.calculateMargins("yearlyTotalPreTaxNetProfits", {
      yearly: true,
    });
  }

  get interestIncomeMargins() {
    return this.calculateMargins("totalInterestIncome");
  }

  get yearlyInterestIncomeMargins() {
    return this.calculateMargins("yearlyTotalInterestIncome", { yearly: true });
  }

  get interestExpenseMargins() {
    return this.calculateMargins("totalInterestExpense");
  }

  get yearlyInterestExpenseMargins() {
    return this.calculateMargins("yearlyTotalInterestExpense", {
      yearly: true,
    });
  }

  get incomeTaxMargins() {
    return this.calculateMargins("totalIncomeTax");
  }

  get yearlyIncomeTaxMargins() {
    return this.calculateMargins("yearlyTotalIncomeTax", { yearly: true });
  }

  get netProfitMargins() {
    return this.calculateMargins("totalAfterTaxNetProfits");
  }

  get yearlyNetProfitMargins() {
    return this.calculateMargins("yearlyTotalAfterTaxNetProfits", {
      yearly: true,
    });
  }

  get EBITDAAsDefinedMargins() {
    return this.calculateMargins("EBITDAAsDefined");
  }

  get yearlyEBITDAAsDefinedMargins() {
    return this.calculateMargins("yearlyEBITDAAsDefined", { yearly: true });
  }

  calculateMargins(source, options = { yearly: false }) {
    const sourceItem = this[source];
    let sales;
    if (options.yearly === false) sales = this.totalSales;
    else sales = this.yearlyTotalSales;

    return sales.map(
      (item, index) =>
        new DateAndValue({
          date: item.date,
          value:
            item.value === 0 || sourceItem.length <= index
              ? 0
              : Number(
                  ((sourceItem[index].value / item.value) * 100).toFixed(2)
                ) || 0,
        })
    );
  }

  getCategoryValues(category, type) {
    const allowedGroupValues = ["incomes", "expenses", "costofsales"];
    if (type && !allowedGroupValues.includes(type))
      throw new Error("Type not valid");

    let allItems = [];
    if (type === "incomes") allItems = [...this.incomes, ...this.otherIncomes];
    else if (type === "costofsales") allItems = [...this.costOfSales];
    else if (type === "expenses")
      allItems = [...this.expenses, ...this.otherExpenses];

    return this.groupByCategoryNameDate(allItems).filter(
      (item) => item.category === category
    );
  }

  getNames(propertyName) {
    const list = new Set();

    this[propertyName].forEach((item) => {
      list.add(item.name);
    });

    return Array.from(list);
  }

  getCategory({ name, type }) {
    const allowedTypes = [
      "costOfSales",
      "incomes",
      "otherIncomes",
      "expenses",
      "otherExpenses",
    ];
    if (!allowedTypes.includes(type)) throw new Error("Type not valid");

    const item = this[type].find((item) => item.name === name);

    if (!item) return null;

    return item.category;
  }

  getItems(propertyName, itemName) {
    const list = this[propertyName].filter((item) => item.name === itemName)

    //there are missing(0) values for some months. We must give zero object and proper name, category, accountId for those months
    let result = this.dates.map((date) => new FinancialAccount({ date }));
    let name;
    let nameArray;
    let category;
    let accountId;

    list.forEach((item) => {
      result.filter((account) => account.date === item.date)[0].value = item.value;
      if(!name) name = item.name;
      if(!category) category = item.category;
      if(!accountId) accountId = item.accountId;
    });

    result.forEach((item) => {
      item.name = name;
      item.category = category;
      item.accountId = accountId;
    })
    
    return this.getOrderedList(result);
  }

  groupByDate(items) {
    const objectList = items.reduce((acc, obj) => {
      const dateOnly = obj.date.split("T")[0]; // Extracting only the date part
      if (!acc[dateOnly]) {
        acc[dateOnly] = {
          date: dateOnly,
          value: 0,
        };
      }
      acc[dateOnly].value += obj.value;
      return acc;
    }, {});
    
    let result = this.dates.map((date) => new DateAndValue({ date: date.split("T")[0], value: 0 }));
      //this function is only used for monthly data
    Object.values(objectList).map((item) => {
      result.filter((account) => account.date === item.date)[0].value = item.value;
    });
    return this.getOrderedList(result);
  }

  calculateTotalByYear(data, options = { applyAverage: false }) {
    const yearTotalMap = {};
    let year = this.descendantDates[0];
    year = typeof year === "string" ? new Date(year) : year;
    if (this.dateOrder === "ASCENDANT_ORDER") data = data.reverse();

    for (const item of data) {
      const value = item.value;

      if (!isNaN(value)) {
        if (!yearTotalMap[year.toLocaleDateString()]) {
          yearTotalMap[year.toLocaleDateString()] = {
            sum: 0,
            count: 0,
          };
        }
        yearTotalMap[year.toLocaleDateString()].sum += value;
        yearTotalMap[year.toLocaleDateString()].count++;
        if (yearTotalMap[year.toLocaleDateString()].count === 12)
          year.setFullYear(year.getFullYear() - 1);
      }
    }

    const result = Object.entries(yearTotalMap).map(
      ([year, { sum, count }]) => ({
        date: DateTime.fromJSDate(new Date(year)).toFormat("LLLL y"),
        value: options.applyAverage ? sum / count : sum,
      })
    );

    return this.getOrderedList(result);
  }

  groupByCategoryNameDate(items) {
    const result = items.reduce((acc, obj) => {
      const key = `${obj.date}-${obj.category}`;
      if (!acc[key]) {
        acc[key] = {
          date: obj.date,
          category: obj.category,
          value: 0,
        };
      }
      acc[key].value += obj.value;
      return acc;
    }, {});

    return Object.values(result);
  }

  formatDateAndValueHash(hash) {
    return Object.keys(hash).reduce((acc, key) => {
      const formattedKey = DateTime.fromISO(key).toFormat("yyyy-LL-01");
      acc[formattedKey] = hash[key];
      return acc;
    }, {});
  }

  getOrderedList(list) {
    if (this.dateOrder === ASCENDANT_ORDER) {
      return list.sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
      });
    }

    return list.sort((a, b) => {
      if (a.date < b.date) return 1;
      if (a.date > b.date) return -1;
    });
  }
}
